import {
  madridDateAddDays,
  madridMonday,
  madridToday,
} from '@/utils/madrid-date';
import type { DailyEarning } from '../types';

export type DayState =
  | 'done'
  | 'fail'
  | 'pending'
  | 'ghost'
  | 'vacation'
  | 'omitted';

export type WeekDay = { iso: string; state: DayState };

type DayCtx = {
  today: string;
  daily: DailyEarning[] | undefined;
  threshold: number;
  ghostBefore?: string;
  vacationFrom?: string;
  vacationTo?: string;
  omittedDates?: string[];
};

function isVacationDay(iso: string, ctx: DayCtx): boolean {
  if (!ctx.vacationFrom || !ctx.vacationTo) return false;
  return iso >= ctx.vacationFrom && iso <= ctx.vacationTo;
}

function isOmittedDay(iso: string, ctx: DayCtx): boolean {
  if (!ctx.omittedDates || ctx.omittedDates.length === 0) return false;
  return ctx.omittedDates.includes(iso);
}

function leadDayState(iso: string, ctx: DayCtx): DayState {
  if (iso > ctx.today) return 'pending';
  const count = ctx.daily?.find((x) => x.date.startsWith(iso))?.lead_count ?? 0;
  if (count >= ctx.threshold) return 'done';
  if (iso === ctx.today) return 'pending';
  return 'fail';
}

function dayState(iso: string, ctx: DayCtx): DayState {
  if (ctx.ghostBefore && iso < ctx.ghostBefore) return 'ghost';
  if (isVacationDay(iso, ctx)) return 'vacation';
  if (isOmittedDay(iso, ctx)) return 'omitted';
  return leadDayState(iso, ctx);
}

export type WeekStatesOpts = {
  joinedIso?: string | undefined;
  streakResumedIso?: string | undefined;
  vacationFrom?: string | undefined;
  vacationTo?: string | undefined;
  omittedDates?: string[] | undefined;
};

function laterIso(a?: string, b?: string): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

function buildCtx(
  daily: DailyEarning[] | undefined,
  threshold: number,
  opts: WeekStatesOpts
): DayCtx {
  const ghostBefore = laterIso(opts.joinedIso, opts.streakResumedIso);
  return {
    today: madridToday(),
    daily,
    threshold,
    ...(ghostBefore ? { ghostBefore } : {}),
    ...(opts.vacationFrom ? { vacationFrom: opts.vacationFrom } : {}),
    ...(opts.vacationTo ? { vacationTo: opts.vacationTo } : {}),
    ...(opts.omittedDates && opts.omittedDates.length > 0
      ? { omittedDates: opts.omittedDates }
      : {}),
  };
}

export function weekStates(
  daily: DailyEarning[] | undefined,
  threshold: number,
  opts: WeekStatesOpts = {}
): WeekDay[] {
  const monday = madridMonday();
  const ctx = buildCtx(daily, threshold, opts);
  return Array.from({ length: 7 }, (_, i) => {
    const iso = madridDateAddDays(monday, i);
    return { iso, state: dayState(iso, ctx) };
  });
}

export function faultsSoFar(days: WeekDay[]): number {
  return days.filter((d) => d.state === 'fail').length;
}
